import React, { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ZINDEX_MODAL_OVERLAY } from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import { TaniumIcon } from '@tanium/react-graphics';
import { Stack } from '@tanium/react-stack';
import { useTheme } from '@tanium/react-theme-context';
import { HEX_292D38 } from '@tanium/colors';
import { AdvancedTooltip, TooltipManager, TooltipReference } from '@tanium/react-tooltip';
import { bluestar } from '@tanium/theme-data';
import { useNavigationDismissDelay } from './hooks/useNavigationDismissDelay';
import { getEmptyActiveRow } from './getEmptyActiveRow';
import { useIsCurrent } from './hooks/useIsCurrent';
import { useNavigationDelayByLevel } from './hooks/useNavigationDelayByLevel';
import { useSharedNavNavItems } from './hooks/useSharedNavNavItems';
import { SideNavIcon } from './SideNavIcon';
import { SideNavLink } from './SideNavLink';
import { ActiveRowInfo, ActiveRows, SideNavType } from './types';
import { shouldProcessMouseEvents } from './utils';
import { ThemeWrapper } from './deps/ThemeWrapper';

const SideNavFill = () => HEX_292D38;
const getAriaControlsValue = (label: string) => `side-nav-sub-menu-${label.replace(' ', '-')}`;
const getAllEmptyRows = (): ActiveRows => [getEmptyActiveRow(), getEmptyActiveRow()];

/**
 * SideNav component code was borrowed from https://git.corp.tanium.com/eng/ufe/tree/main/packages/ufe-shell/src/components/SideNav
 * Changes were made to fit our use case but the code was primarily source from UFE
 */
export const SideNav = () => {
  const [activeRows, setActiveRows] = useState<ActiveRows>(getAllEmptyRows());

  const theme = useTheme();
  const NAV_ITEM_HEIGHT_PX = 37;
  const ICON_SIZE_PX = 18;
  const ICON_X_PADDING_PX = theme.space[2];
  const SIDENAV_WIDTH_PX = ICON_SIZE_PX + 2 * ICON_X_PADDING_PX;

  const scrollingContainerRef0 = useRef<HTMLDivElement>(null);

  const [isFocusInNavigation, setIsFocusInNavigation] = useState(false);

  const location = useLocation();
  const currentUrl = location.pathname + location.search;

  const setActiveRowsByIndex = useCallback(
    (newActiveRows: Record<number, ActiveRowInfo>) => {
      if (shouldProcessMouseEvents()) {
        setActiveRows((state) => Object.assign([], state, newActiveRows));
      }
    },
    [setActiveRows]
  );

  const activeRowInfo0 = activeRows[0];

  const {
    triggerProps: { onMouseEnterWrapper: handleEnterTrigger0, clearActiveRowTimeout: clearEnterTrigger0Timeout },
  } = useNavigationDelayByLevel(0, setActiveRowsByIndex);

  const handleFocusTrigger0 = (activeIndex: number) => (e: React.FocusEvent<HTMLElement>) => {
    setIsFocusInNavigation(true);
    setActiveRowsByIndex({ 0: { index: activeIndex, element: e.currentTarget } });
  };

  const resetActiveRows = useCallback(() => {
    setActiveRows(getAllEmptyRows());
    clearEnterTrigger0Timeout();
  }, [clearEnterTrigger0Timeout]);

  const handleLeaveNavigation = useCallback(() => {
    setIsFocusInNavigation(false);
  }, []);

  const { clearDismissTimeout } = useNavigationDismissDelay({
    onNavigationLeave: handleLeaveNavigation,
  });

  const handleEnterNavItemsArea = useCallback(() => {
    if (shouldProcessMouseEvents()) {
      clearDismissTimeout();
    }
  }, [clearDismissTimeout]);

  const handleSideNavLinkButtonActivated = useCallback(
    (activeIndex: number) => (e: SyntheticEvent<HTMLSpanElement>) => {
      if (e.type === 'click') e.stopPropagation();
      if (activeRowInfo0.index === -1) {
        setActiveRowsByIndex({ 0: { index: activeIndex, element: e.currentTarget } });
      }
    },
    [activeRowInfo0.index, setActiveRowsByIndex]
  );

  const navItems = useSharedNavNavItems();
  const navigationRef = useRef<HTMLDivElement | null>(null);
  const checkIsFocusOutsideNavigation = useCallback(
    (e: FocusEvent) => {
      const isNewFocusInNavigation = e.target instanceof Node && navigationRef.current?.contains(e.target);
      if (isFocusInNavigation && !isNewFocusInNavigation) {
        handleLeaveNavigation();
      }
    },
    [handleLeaveNavigation, isFocusInNavigation]
  );

  useEffect(() => {
    window.addEventListener('click', handleLeaveNavigation);
    window.addEventListener('focusin', checkIsFocusOutsideNavigation);
    return () => {
      window.removeEventListener('click', handleLeaveNavigation);
      window.removeEventListener('focusin', checkIsFocusOutsideNavigation);
    };
  }, [checkIsFocusOutsideNavigation, handleLeaveNavigation]);

  const { isCurrent } = useIsCurrent({ navItems, currentUrl });

  return (
    <ThemeWrapper
      theme={bluestar}
      height="100%"
      position="relative"
      width={SIDENAV_WIDTH_PX}
      zIndex={ZINDEX_MODAL_OVERLAY}
    >
      <Box position="absolute" top={0} bottom={0} tabIndex={-1} ref={navigationRef}>
        <Box backgroundColor={SideNavFill} height="100%" width={`${SIDENAV_WIDTH_PX}px`} overflowY="hidden">
          <Stack
            direction="column"
            onMouseEnter={handleEnterNavItemsArea}
            onMouseLeave={resetActiveRows}
            ref={scrollingContainerRef0}
          >
            {navItems.map((navItem, i) => {
              const { label, icon: Icon, type } = navItem;
              const { href } = navItem;
              const shouldShowLabelTooltip = type !== SideNavType.Home;

              return (
                <React.Fragment key={label}>
                  <TooltipManager>
                    <TooltipReference>
                      {({ ref }) => (
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        <div onMouseEnter={handleEnterTrigger0(i)} onFocus={handleFocusTrigger0(i)} ref={ref}>
                          <SideNavLink
                            aria-label={label}
                            aria-expanded={href ? undefined : activeRowInfo0.index === i}
                            aria-controls={href ? undefined : getAriaControlsValue(label)}
                            isCurrent={isCurrent(i)}
                            isSelected={activeRowInfo0.index === i}
                            to={navItem.disabled ? undefined : href}
                            onActivate={handleSideNavLinkButtonActivated(i)}
                          >
                            <Box height={NAV_ITEM_HEIGHT_PX} display="flex" alignItems="stretch">
                              <Box
                                pl={`${ICON_X_PADDING_PX}px`}
                                pr={`${ICON_X_PADDING_PX}px`}
                                display="flex"
                                alignItems="center"
                              >
                                <Box
                                  position="relative"
                                  display="inline-flex"
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  <></>
                                  <SideNavIcon isCurrent={isCurrent(i)} icon={Icon || TaniumIcon} size={ICON_SIZE_PX} />
                                </Box>
                              </Box>
                            </Box>
                          </SideNavLink>
                        </div>
                      )}
                    </TooltipReference>
                    {shouldShowLabelTooltip && i === activeRowInfo0.index && (
                      <AdvancedTooltip placement="right">{label}</AdvancedTooltip>
                    )}
                  </TooltipManager>
                </React.Fragment>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </ThemeWrapper>
  );
};
